import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Sparkles, Wand2, Package, BarChart3, Search,
    TrendingUp, ShieldCheck, Layers, Clock, Star, ArrowRight,
    Brain,
    Crown
} from 'lucide-react';

const ComingSoonCard = ({ icon: Icon, title, description, badges = [], gradient = 'from-gray-400 to-gray-500' }) => (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-200 p-6 relative overflow-hidden group hover:shadow-xl transition-all duration-300">
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-1 rounded-full">
            <Clock size={10} /> Coming Soon
        </div>
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 shadow-lg opacity-60`}>
            <Icon className="text-white" size={22} />
        </div>
        <h3 className="text-base font-bold text-gray-700 mb-1">{title}</h3>
        <p className="text-sm text-gray-400 leading-relaxed mb-3">{description}</p>
        {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
                {badges.map((b, i) => (
                    <span key={i} className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">{b}</span>
                ))}
            </div>
        )}
        <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] rounded-2xl opacity-0 group-hover:opacity-0" />
    </div>
);

const ActiveCard = ({ icon: Icon, title, description, tags = [], onClick, gradient = 'from-teal-500 to-blue-600', badge }) => (
    <div
        onClick={onClick}
        className="bg-white rounded-2xl shadow-lg border-2 border-transparent hover:border-teal-400 p-6 cursor-pointer relative overflow-hidden group hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
    >
        {badge && (
            <div className="absolute top-3 right-3 flex items-center gap-1 bg-green-100 text-green-700 text-[10px] font-bold px-2 py-1 rounded-full">
                <Star size={10} className="fill-green-500" /> {badge}
            </div>
        )}
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="text-white" size={22} />
        </div>
        <h3 className="text-base font-bold text-gray-800 mb-1 flex items-center gap-2">
            {title}
            <ArrowRight size={14} className="text-teal-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200" />
        </h3>
        <p className={`${title === "Brands Hunter" ? "text-xl text-black" : "text-sm text-gray-500"} leading-relaxed mb-3`}>{description}</p>
        {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
                {tags.map((t, i) => (
                    <span key={i} className="text-[10px] bg-teal-50 text-teal-600 border border-teal-200 px-2 py-0.5 rounded-full font-bold">{t}</span>
                ))}
            </div>
        )}
    </div>
);

const BrandDashboardPage = ({ user }) => {
    const navigate = useNavigate();
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [passwordInput, setPasswordInput] = useState('');
    const [error, setError] = useState(false);

    const CORRECT_PASSWORD = 'idobrands';

    const handleUnlock = (e) => {
        e.preventDefault();
        if (passwordInput === CORRECT_PASSWORD) {
            setIsUnlocked(true);
            setError(false);
        } else {
            setError(true);
        }
    };

    if (!isUnlocked) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4 -mt-24">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Sparkles size={26} className="text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Multi Brand Pro</h2>
                    <p className="text-sm text-gray-500 mb-6">Enter the secret access code your invitee gave you</p>
                    <form onSubmit={handleUnlock}>
                        <input
                            type="password"
                            value={passwordInput}
                            onChange={(e) => {
                                setPasswordInput(e.target.value);
                                setError(false);
                            }}
                            placeholder="Access code"
                            autoFocus
                            className={`w-full px-4 py-3 rounded-xl border-2 mb-3 text-center outline-none transition ${error ? 'border-red-400' : 'border-gray-200 focus:border-teal-400'
                                }`}
                        />
                        {error && (
                            <p className="text-red-500 text-xs mb-3">Incorrect access code, try again.</p>
                        )}
                        <button
                            type="submit"
                            className="w-full py-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white font-bold rounded-xl hover:opacity-90 transition"
                        >
                            Unlock
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-20">

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn { animation: fadeIn 0.4s ease both; }
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-12px); }
                }
                .animate-float { animation: float 4s ease-in-out infinite; }
            `}</style>

            {/* Hero */}
            <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white px-4 pt-12 pb-16 relative overflow-hidden">
                <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-b from-transparent to-slate-50 z-10 pointer-events-none" />
                <div className="absolute inset-0 opacity-10">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="absolute rounded-full bg-white animate-float"
                            style={{ width: `${60 + i * 30}px`, height: `${60 + i * 30}px`, left: `${10 + i * 15}%`, top: `${10 + (i % 3) * 25}%`, animationDelay: `${i * 0.6}s` }} />
                    ))}
                </div>
                <div className="max-w-5xl mx-auto relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shadow-xl">
                            <Sparkles size={24} className="text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold leading-tight">Multi Brand Pro</h1>
                        </div>
                    </div>
                    <p className="text-white text-base max-w-xl leading-relaxed">
                        The complete brand intelligence suite - find influencers, generate stunning product images, manage inventory, optimize SEO, and dominate your category on Amazon & Walmart.
                    </p>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 mt-6 relative z-10">
                {/* Active Tools */}
                <div className="mb-10 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-teal-500 to-blue-600 rounded-full" />
                        <h2 className="text-xl font-extrabold text-gray-900">Available Now</h2>
                        <span className="ml-2 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">Live</span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        <ActiveCard
                            icon={Crown}
                            title="Brands Hunter"
                            description="Find brands that need your help."
                            tags={['Brand Discovery', 'Brand Finder', 'Brand Direct']}
                            badge="New"
                            gradient="from-green-500 to-green-600"
                            onClick={() => navigate('/brandhunt')}
                        />
                        <ActiveCard
                            icon={Search}
                            title="Influencer Hunter"
                            description="Find Amazon influencers already reviewing products in your niche. Swipe through creator videos, save your favorites, and reach out directly via their social profiles."
                            tags={['Influencer Discovery', 'Social Finder', 'Video Swipe']}
                            badge="New"
                            gradient="from-teal-500 to-blue-600"
                            onClick={() => navigate('/infhunt')}
                        />
                        <ActiveCard
                            icon={Wand2}
                            title="Product Photo Studio"
                            description="Upload any ASIN and generate professional studio shots, lifestyle scenes, and branded content using AI. Stand out on the listing page instantly."
                            tags={['AI Images', 'Studio Shots', 'Lifestyle Scenes']}
                            badge="Preview"
                            gradient="from-purple-500 to-pink-600"
                            onClick={() => navigate('/brandimages')}
                        />
                    </div>
                </div>

                {/* Coming Soon - Inventory */}
                <div className="mb-10 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full" />
                        <h2 className="text-xl font-extrabold text-gray-900">MSP Inventory Suite</h2>
                        <span className="ml-2 bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">Coming Soon</span>
                    </div>
                    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5 mb-5">
                        <p className="text-sm text-amber-800 font-medium">🏗️ Full inventory management for Amazon & Walmart - all in one place. Accounting, reimbursements, sales tax, and more.</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <ComingSoonCard icon={Layers} title="Inventory Management" description="Real-time stock levels, reorder alerts, and multi-warehouse tracking for Amazon FBA & Walmart WFS." badges={['Amazon', 'Walmart', 'FBA', 'WFS']} gradient="from-orange-400 to-amber-500" />
                        <ComingSoonCard icon={BarChart3} title="Accounting & Reports" description="P&L statements, fee breakdowns, COGS tracking, and automated monthly reports exported to your accountant." badges={['P&L', 'COGS', 'Fees', 'Export']} gradient="from-amber-500 to-yellow-500" />
                        <ComingSoonCard icon={ShieldCheck} title="Reimbursements" description="Automatically detect lost, damaged, or overcharged FBA inventory and file reimbursement claims on your behalf." badges={['Auto-Detect', 'FBA Claims', 'Money Back']} gradient="from-green-500 to-emerald-600" />
                        <ComingSoonCard icon={Package} title="Sales Tax Reports" description="Multi-state reports across all your selling channels." badges={['Multi-State', 'Reports', 'Multi-Channel']} gradient="from-blue-400 to-indigo-500" />
                        <ComingSoonCard icon={TrendingUp} title="Forecasting & Replenishment" description="AI-powered demand forecasting tells you exactly when and how much to reorder to never go out of stock." badges={['AI Forecast', 'Reorder Alerts', 'Seasonal']} gradient="from-purple-400 to-violet-500" />
                        <ComingSoonCard icon={Layers} title="Supplier Management" description="Track purchase orders, lead times, and supplier performance in one dashboard." badges={['POs', 'Lead Times', 'Suppliers']} gradient="from-pink-400 to-rose-500" />
                    </div>
                </div>

                {/* Coming Soon - SEO & Content */}
                <div className="mb-10 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full" />
                        <h2 className="text-xl font-extrabold text-gray-900">SEO & Content Optimization</h2>
                        <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full">Coming Soon</span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <ComingSoonCard icon={Search} title="Listing SEO Optimizer" description="AI rewrites your title, bullets, and description with the highest-converting keywords in your category." badges={['AI Writing', 'Keywords', 'A+ Content']} gradient="from-blue-500 to-indigo-600" />
                        <ComingSoonCard icon={TrendingUp} title="Keyword Rank Tracker" description="Track your organic keyword rankings daily across Amazon and Walmart and get alerts when you move." badges={['Daily Tracking', 'Alerts', 'History']} gradient="from-indigo-500 to-purple-600" />
                        <ComingSoonCard icon={Star} title="Review & Q&A Intelligence" description="Monitor reviews, detect trends in customer sentiment, and auto-generate responses to Q&A." badges={['Sentiment', 'Auto-Reply', 'Alerts']} gradient="from-purple-500 to-pink-600" />
                    </div>
                </div>

                {/* Coming Soon - Competition */}
                <div className="mb-10 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-red-500 to-rose-600 rounded-full" />
                        <h2 className="text-xl font-extrabold text-gray-900">Competition Analysis</h2>
                        <span className="ml-2 bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">Coming Soon</span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <ComingSoonCard icon={BarChart3} title="Competitor Price Tracker" description="Monitor competitor pricing 24/7 and get instant alerts when they undercut you or run promotions." badges={['24/7 Alerts', 'Price History', 'Promotions']} gradient="from-red-500 to-rose-600" />
                        <ComingSoonCard icon={Search} title="Market Share Analysis" description="See exactly how your brand stacks up against competitors in sales volume, reviews, and visibility." badges={['Market Share', 'Sales Est.', 'Visibility']} gradient="from-rose-500 to-pink-600" />
                        <ComingSoonCard icon={TrendingUp} title="Category Trend Spotter" description="Identify rising trends in your category before they peak so you can be first to market." badges={['Trend Detection', 'Early Mover', 'AI Insights']} gradient="from-pink-500 to-fuchsia-600" />
                    </div>
                </div>

                {/* Coming Soon - Research */}
                <div className="mb-10 animate-fadeIn">
                    <div className="flex items-center gap-2 mb-5">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-green-500 to-teal-600 rounded-full" />
                        <h2 className="text-xl font-extrabold text-gray-900">Amazon & Walmart Research Tools</h2>
                        <span className="ml-2 bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">Coming Soon</span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <ComingSoonCard icon={Search} title="Product Opportunity Finder" description="Discover untapped product opportunities with high demand, low competition, and strong margins." badges={['Opportunity Score', 'Demand', 'Margins']} gradient="from-green-500 to-emerald-600" />
                        <ComingSoonCard icon={Package} title="Brand Analytics Pro" description="Deep dive into your brand's performance with data pulled directly from Amazon Brand Analytics and Walmart Connect." badges={['Brand Analytics', 'Search Terms', 'Market Basket']} gradient="from-emerald-500 to-teal-600" />
                        <ComingSoonCard icon={BarChart3} title="Ad Intelligence" description="Spy on competitor Sponsored Product ads, analyze their spend, and find winning ad strategies for your brand." badges={['PPC Spy', 'Ad Copy', 'Bidding']} gradient="from-teal-500 to-cyan-600" />
                    </div>
                </div>

                {/* CTA Banner */}
                <div className="animate-fadeIn bg-gradient-to-r from-teal-600 to-blue-600 rounded-3xl p-8 text-white text-center shadow-2xl">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                        <Sparkles size={28} className="text-white" />
                    </div>
                    <h3 className="text-2xl font-extrabold mb-2">Want Early Access?</h3>
                    <p className="text-white/80 text-sm max-w-md mx-auto mb-6">Be the first to access our full Brand Suite. Enterprise pricing available for agencies and large sellers.</p>
                    <a
                        href="mailto:contact@multisellerpro.com"
                        className="inline-block px-8 py-3 bg-white text-blue-700 font-bold rounded-xl hover:bg-white/90 transition shadow-xl text-sm"
                    >
                        Contact Us for Enterprise Access →
                    </a>
                </div>

            </div>
        </div>
    );
};

export default BrandDashboardPage;